import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GetApp as GetAppIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import dayjs from "dayjs";

import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";

const fmt = (v) =>
  (v || 0).toLocaleString("pt-CV", { minimumFractionDigits: 0 });
const fmtCVE = (v) => `${fmt(v)} CVE`;
const fmtData = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");
// Linhas de orçamento MISTO trazem null: não há como saber quanto é do paciente
// e quanto é da instituição, por isso mostra-se "?".
const fmtCel = (v) => (v === null || v === undefined ? "?" : fmt(v));
// No Excel mantém-se o número cru (para as fórmulas funcionarem); só o
// indeterminado vira texto.
const fmtCelExcel = (v) => (v === null || v === undefined ? "?" : v);

/* ---------------------------------------------------------------------------
 * As colunas NÃO são definidas aqui — vêm do backend em `res.colunas`
 * (ver COLUNAS_RELATORIO em relatoriosFinanceiro-controllers.mjs). Para
 * acrescentar, remover, reordenar ou renomear colunas, mexe-se só no backend.
 *
 * Este ficheiro só sabe COMO desenhar cada tipo de célula, não QUAIS existem.
 * ------------------------------------------------------------------------- */

// Valor de uma célula no ecrã.
const celulaEcra = (col, linha) => {
  if (col.preenchidoAMao) return "—";
  const valor = linha[col.chave];
  if (col.tipo === "moeda") return fmtCel(valor);
  return valor ?? "-";
};

// Valor de uma célula no Excel.
const celulaExcel = (col, linha) => {
  if (col.preenchidoAMao) return "";
  const valor = linha[col.chave];
  if (col.tipo === "descricao") {
    return [linha.descricao, linha.referencia].filter(Boolean).join(" — ");
  }
  if (col.tipo === "moeda") return fmtCelExcel(valor);
  return valor ?? "";
};

function RelatorioDiarioAtendimento({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
}) {
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [dados, setDados] = useState(null);
  const [utilizadores, setUtilizadores] = useState([]);
  const [selecionados, setSelecionados] = useState([]);

  const clinicaId = dadosClinica?._id;
  // Guarda os dados mais recentes para o export não depender do ciclo de render.
  const dadosRef = useRef(null);
  dadosRef.current = dados;

  useEffect(() => {
    if (!clinicaId) return;
    let vivo = true;
    const fetchUtilizadores = async () => {
      try {
        const resp = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/users/ativos/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (!vivo) return;
        setUtilizadores(
          (resp.users || []).map((u) => ({ id: u.id, nome: u.name })),
        );
      } catch (err) {
        console.error("err", err);
      }
    };
    fetchUtilizadores();
    return () => {
      vivo = false;
    };
  }, [clinicaId, auth.token, sendRequest]);

  const buscar = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!clinicaId) return null;
      startLoading();
      try {
        // Relatório de um único dia: o filtro mostra só a Data Final (que
        // arranca em hoje), por isso o dia é sempre esse.
        const dia = dayjs(dataFiltro.dataFinal).format("YYYY-MM-DD");
        const params = new URLSearchParams({
          dataInicial: dia,
          dataFinal: dia,
        });
        if (selecionados.length) {
          params.set("utilizadores", selecionados.join(","));
        }
        // Sem unidade escolhida o servidor devolve as do utilizador — que para
        // quem tem acesso às duas continua a ser o consolidado de sempre.
        if (unidadeSelecionada) {
          params.set("estabelecimento", unidadeSelecionada);
        }
        const resp = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/relatoriosfinanceiro/diarioatendimento/${clinicaId}?${params}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (!resp?.success) throw new Error("Resposta inválida");
        setDados(resp);
        dadosRef.current = resp;
        if (!silencioso) {
          if (resp.resumo.totalLinhas === 0) {
            toast("Sem recebimentos no período.");
          } else {
            toast.success("Relatório gerado com sucesso!");
          }
        }
        return resp;
      } catch (err) {
        console.error("err", err);
        toast.error("Erro ao gerar o diário de atendimentos");
        return null;
      } finally {
        stopLoading();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clinicaId, dataFiltro, selecionados, unidadeSelecionada, auth.token],
  );

  const exportarExcel = useCallback(async () => {
    const res = dadosRef.current || (await buscar({ silencioso: true }));
    if (!res || res.resumo.totalLinhas === 0) {
      toast.error("Sem dados para exportar!");
      return;
    }

    const nomeClinica = (
      dadosClinica?.nomeFiscal ||
      dadosClinica?.nome ||
      ""
    ).toUpperCase();

    const colunas = res.colunas || [];
    const ultimaCol = Math.max(0, colunas.length - 1);
    // Índice onde assentam os rótulos "TOTAL …": a coluna da descrição.
    const idxRotuloTotal = Math.max(
      0,
      colunas.findIndex((c) => c.tipo === "descricao"),
    );

    // Constrói a linha de totais a partir das colunas somáveis.
    const linhaDeTotais = (rotulo, totais) =>
      colunas.map((c, i) => {
        if (i === idxRotuloTotal) return rotulo;
        if (c.somavel) return totais[c.chave] ?? "";
        if (c.chave === "observacoes" && totais.indeterminado) {
          return `Misto (por repartir): ${fmt(totais.indeterminado)}`;
        }
        return "";
      });

    const linhasFolha = [
      [`${res.titulo || "DIÁRIO DE ATENDIMENTOS"} DA ${nomeClinica}`],
      [`Data: ${fmtData(dataFiltro.dataFinal)}`],
      [],
    ];

    // Cabeçalho agrupado: cada linha da matriz vira uma linha da folha, e as
    // células com colSpan/rowSpan viram merges.
    const cabecalho = res.cabecalho || [];
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: ultimaCol } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: ultimaCol } },
    ];
    const linhasCabecalho = [];
    const OCUPADA = "__OCUPADA__"; // sentinela interno, trocado por "" no fim

    const escreverCabecalho = (linhaBase) => {
      const grelha = cabecalho.map(() => new Array(colunas.length).fill(""));

      cabecalho.forEach((celulasDoNivel, nivel) => {
        let col = 0;
        celulasDoNivel.forEach((cel) => {
          // Salta colunas já tomadas por um rowSpan vindo de cima.
          while (col < colunas.length && grelha[nivel][col] !== "") col++;

          // Reserva toda a área da célula...
          for (let r = nivel; r < nivel + cel.rowSpan; r++) {
            for (let c = col; c < col + cel.colSpan; c++) {
              if (grelha[r]) grelha[r][c] = OCUPADA;
            }
          }
          // ...e escreve o título no canto superior esquerdo.
          grelha[nivel][col] = cel.titulo;

          if (cel.colSpan > 1 || cel.rowSpan > 1) {
            merges.push({
              s: { r: linhaBase + nivel, c: col },
              e: {
                r: linhaBase + nivel + cel.rowSpan - 1,
                c: col + cel.colSpan - 1,
              },
            });
          }
          col += cel.colSpan;
        });
      });

      grelha.forEach((l) =>
        linhasFolha.push(l.map((v) => (v === OCUPADA ? "" : v))),
      );
    };

    res.grupos.forEach((grupo, idx) => {
      if (idx > 0) linhasFolha.push([]);
      linhasFolha.push([`Recebido por: ${grupo.utilizadorNome}`]);
      linhasCabecalho.push(linhasFolha.length);
      escreverCabecalho(linhasFolha.length);
      grupo.linhas.forEach((l) => {
        linhasFolha.push(colunas.map((c) => celulaExcel(c, l)));
      });
      linhasFolha.push(
        linhaDeTotais(`TOTAL — ${grupo.utilizadorNome}`, grupo.totais),
      );
    });

    const g = res.totaisGerais;
    linhasFolha.push([]);
    linhasFolha.push(linhaDeTotais("TOTAL GERAL", g));

    const ws = XLSX.utils.aoa_to_sheet(linhasFolha);
    ws["!cols"] = colunas.map((c) => ({ wch: c.larguraExcel || 14 }));
    ws["!merges"] = merges;

    // Índices das linhas ocupadas pelo cabeçalho agrupado (podem ser várias
    // por grupo de utilizador).
    const linhasDeCabecalho = new Set();
    linhasCabecalho.forEach((base) => {
      for (let n = 0; n < cabecalho.length; n++) linhasDeCabecalho.add(base + n);
    });

    linhasFolha.forEach((linha, r) => {
      const primeira = String(linha[0] ?? "");
      const ehCabecalho = linhasDeCabecalho.has(r);
      const ehTotal = String(linha[idxRotuloTotal] ?? "").startsWith("TOTAL");
      const ehGrupo = primeira.startsWith("Recebido por:");
      linha.forEach((_, c) => {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) return;
        if (r === 0) {
          ws[ref].s = {
            font: { bold: true, sz: 13 },
            alignment: { horizontal: "center" },
          };
        } else if (ehCabecalho) {
          ws[ref].s = {
            font: { bold: true, sz: 10 },
            fill: { fgColor: { rgb: "D9EAD3" } },
            alignment: { horizontal: "center", wrapText: true },
          };
        } else if (ehTotal) {
          ws[ref].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "F0F0F0" } },
          };
        } else if (ehGrupo) {
          ws[ref].s = { font: { bold: true, sz: 11 } };
        }
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Diário de Atendimentos");
    XLSX.writeFile(
      wb,
      `DiarioAtendimentos_${dayjs(dataFiltro.dataFinal).format("DD-MM-YYYY")}.xlsx`,
    );
    toast.success("Excel exportado com sucesso!");
  }, [buscar, dadosClinica, dataFiltro]);

  useEffect(() => {
    if (acao === "gerar") buscar();
    else if (acao === "exportar") exportarExcel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizacoes]);

  if (!dados) {
    return (
      <Typography sx={{ p: 2 }} color="text.secondary">
        Selecione o período e clique em "Gerar Relatório" para ver os
        recebimentos do dia agrupados por utilizador.
      </Typography>
    );
  }

  const g = dados.totaisGerais;
  // Colunas visíveis no ecrã (as marcadas `apenasExcel` só saem no ficheiro).
  const colunasEcra = (dados.colunas || []).filter((c) => !c.apenasExcel);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Controlos */}
      <Card sx={{ mb: 2 }}>
        <CardContent
          sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}
        >
          <FormControl sx={{ minWidth: 280 }} size="small">
            <InputLabel id="lbl-utilizadores">
              Filtrar por utilizador (recebido por)
            </InputLabel>
            <Select
              labelId="lbl-utilizadores"
              multiple
              value={selecionados}
              onChange={(e) =>
                setSelecionados(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              input={
                <OutlinedInput label="Filtrar por utilizador (recebido por)" />
              }
              renderValue={(sel) =>
                sel.length === 0
                  ? "Todos os utilizadores"
                  : sel
                      .map(
                        (id) =>
                          utilizadores.find((u) => u.id === id)?.nome || id,
                      )
                      .join(", ")
              }
            >
              {utilizadores.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" onClick={() => buscar()}>
            Atualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={exportarExcel}
          >
            Exportar Excel
          </Button>
        </CardContent>
      </Card>

      {/* Resumo */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { titulo: "Dinheiro", valor: g.dinheiro },
          { titulo: "POS", valor: g.pos },
          {
            titulo: "Total recebido",
            valor: g.pacienteTotal,
            cor: "success.main",
          },
        ].map((card) => (
          <Grid item xs={12} md={4} key={card.titulo}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {card.titulo}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: card.cor }}
                >
                  {fmtCVE(card.valor)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {dados.resumo.linhasOrigemDesconhecida > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {dados.resumo.linhasOrigemDesconhecida} recebimento(s) sem ligação a um
          orçamento, tratamento isolado, consulta ou avaliação — aparecem
          marcados como <strong>origem desconhecida</strong>.
        </Alert>
      )}

      {dados.grupos.length === 0 && (
        <Typography sx={{ p: 2 }} color="text.secondary">
          Sem recebimentos no período selecionado.
        </Typography>
      )}

      {/* Uma tabela por utilizador */}
      {dados.grupos.map((grupo) => (
        <Card key={grupo.utilizadorId || "sem-utilizador"} sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Recebido por: {grupo.utilizadorNome}
              </Typography>
              <Chip
                variant="outlined"
                label={`${grupo.linhas.length} recebimento(s) · ${fmtCVE(
                  grupo.totais.total,
                )}`}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  {(dados.cabecalhoEcra || []).map((linhaCab, nivel) => (
                    <TableRow key={nivel}>
                      {linhaCab.map((cel, idx) => (
                        <TableCell
                          key={`${nivel}-${idx}-${cel.titulo}`}
                          colSpan={cel.colSpan}
                          rowSpan={cel.rowSpan}
                          align="center"
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            verticalAlign: "middle",
                            fontWeight: "bold",
                            ...(cel.ehGrupo && {
                              backgroundColor: "action.hover",
                            }),
                          }}
                        >
                          {cel.titulo}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {grupo.linhas.map((l) => (
                    <TableRow key={l.chaveLinha} hover>
                      {colunasEcra.map((c) =>
                        c.tipo === "descricao" ? (
                          <TableCell key={c.chave}>
                            <Typography
                              variant="body2"
                              noWrap
                              title={l.descricao}
                            >
                              {l.descricao}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                alignItems: "center",
                                mt: 0.3,
                              }}
                            >
                              {l.nrPagamentos > 1 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${l.nrPagamentos} pagamentos`}
                                />
                              )}
                              {l.referencia && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {l.referencia}
                                </Typography>
                              )}
                              {!l.origemConhecida && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label="origem desconhecida"
                                />
                              )}
                              {l.origemConhecida && l.cobertura && (
                                <Chip size="small" label={l.cobertura} />
                              )}
                            </Box>
                          </TableCell>
                        ) : (
                          <TableCell
                            key={c.chave}
                            align={c.tipo === "moeda" ? "right" : "left"}
                            sx={{
                              ...(c.preenchidoAMao && { color: "text.disabled" }),
                              ...(c.destaque === "info" && {
                                color: "info.main",
                              }),
                              ...(c.negrito && { fontWeight: "bold" }),
                            }}
                          >
                            {celulaEcra(c, l)}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  ))}

                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    {colunasEcra.map((c, i) => (
                      <TableCell
                        key={c.chave}
                        align={c.tipo === "moeda" ? "right" : "left"}
                        sx={{
                          fontWeight: "bold",
                          ...(c.preenchidoAMao && { color: "text.disabled" }),
                        }}
                      >
                        {i === 0
                          ? "Total"
                          : c.preenchidoAMao
                            ? "—"
                            : c.somavel
                              ? fmt(grupo.totais[c.chave])
                              : ""}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {fmt(grupo.totais.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

export default RelatorioDiarioAtendimento;
